import React from 'react';
import { Link } from 'react-router-dom';
import styled, { css } from 'styled-components';
import { darken, lighten } from 'polished';

import Icon from './Icon';

const buttonStyles = css`
  background-color: ${props => props.theme.btnDefaultBg};
  border: 1px solid #CCCCCC;
  border-radius: 8px;
  color: ${props => props.theme.btnDefaultColor};
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  display: inline-block;
  font-weight: 600;
  font-size: 18px;
  padding: 0.75em 2em;
  text-align: center;
  text-decoration: none;
  transition: 150ms linear all;

  ${props => !props.disabled && css`
    &:hover, &:focus {
      background-color: ${props => darken(0.05, props.theme.btnDefaultBg)};
    }
  `}

  ${props => (props.size === 'sm' || props['data-size'] === 'sm') && css`
    font-size: 14px;
    padding: 0.5em 1.25em;
  `}

  ${props => props.size === 'sq' && css`
    height: 44px;
    padding: 0.5em;
    width: 44px;
  `}

  ${props => (props.block || props['data-block']) && css`
    display: block;
    padding-left: 1em;
    padding-right: 1em;
    margin-bottom: 0.5em;
    width: 100%;
  `}

  ${props => (props.primary || props['data-primary']) && css`
    background-color: ${props.theme.btnPrimaryBg};
    border-color: transparent;
    color: ${props.disabled ? lighten(0.35, props.theme.btnPrimaryColor) : props.theme.btnPrimaryColor};
    ${!props.disabled && css`
      &:hover, &:focus {
        background-color: ${props => lighten(0.05, props.theme.btnPrimaryBg)};
      }
    `}
  `}

  ${props => (props.secondary || props['data-secondary']) && css`
    background-color: ${props.theme.btnSecondaryBg};
    border-color: transparent;
    color: ${props.theme.btnSecondaryColor};
    &:hover, &:focus {
      background-color: ${lighten(0.05, props.theme.btnSecondaryBg)};
    }
  `}

  ${props => (props.transparent || props['data-transparent']) && css`
    background-color: transparent;
    border-color: transparent;
  `}

`;

const Button = styled.button`${buttonStyles}`;
const LinkButton = Button.withComponent('a');
const RouteButton = styled(Link)`${buttonStyles}`;
const ActionButtonStyled = styled(Button)`color: ${props => props.theme.brand}`;
const ActionButtonStyledLink = ActionButtonStyled.withComponent(LinkButton);

const ActionButton = ({ children, icon, href = null, ...props }) => {
  const Component = href ? ActionButtonStyledLink : ActionButtonStyled;
  return (
    <Component size="sm" href={href} {...props}>
      <Icon size={18} type={icon} withMargin={true} />
      {children}
    </Component>
  );
};

const submitStyles = css`
  background-color: ${props => props.theme.submitButtonBg};
  border: 0;
  bottom: 0;
  color: ${props => props.disabled ? props.theme.submitButtonDisabledColor: 'white'};
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  display: block;
  font-size: 18px;
  font-weight: bold;
  height: ${props => props.theme.submitButtonHeight};
  margin: auto;
  padding: 0;
  position: fixed;
  text-decoration: none;
  text-transform: uppercase;
  transition: 150ms linear all;
  width: 100%;
  z-index: 1;

  ${props => !props.disabled && css`
    &:hover {
      background-color: ${props => darken(0.05, props.theme.submitButtonBg)};
    }
  `};

  line-height: ${props => props.theme.submitButtonHeight};
  text-align: center;
  text-decoration: none;

  ${window.Modernizr.flexbox && css`
    align-items: center;
    display: flex;
    line-height: inherit;
    justify-content: center;
  `}
`;

const SubmitButton = styled.button`
  ${submitStyles}
  a {
    color: ${props => props.disabled ? props.theme.submitButtonDisabledColor: 'white'};
    display: block;
    height: 100%;
    line-height: ${props => props.theme.submitButtonHeight};
    text-align: center;
    text-decoration: none;
    width: 100%;

    ${window.Modernizr.flexbox && css`
      align-items: center;
      display: flex;
      line-height: inherit;
      justify-content: center;
    `}
  }
`;

const ExternalLinkButton = styled.a`
  background-color: #D9D9D9;
  border-radius: 20px;
  color: ${props => props.theme.brand};
  display: inline-block;
  font-size: 16px;
  font-weight: 600;
  padding: 0.2em 1em;
  text-align: center;
  text-decoration: none;
  transition: 150ms linear all;
  &:hover {
    background-color: ${lighten(0.15, '#D9D9D9')};
  }
  span[class^="ss-"] {
    vertical-align: middle;
  }
  span + span[class^="ss-"],
  span[class^="ss-"] + span {
    margin-left: 0.15em;
  }
`;

export {
  ActionButton,
  Button,
  buttonStyles,
  ExternalLinkButton,
  LinkButton,
  RouteButton,
  SubmitButton,
  submitStyles
};
export default Button;



// WEBPACK FOOTER //
// ./src/components/Button.js